"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Img } from "@/components/image";
import { AddonSellerName } from "@/features/addons/components/addon-seller-name";
import { useIsTextClamped } from "@/lib/hooks/use-is-text-clamped";
import { cn } from "@/lib/utils";

type DietAddonCardDetailsProps = {
  addon: AddonDietAddonsRead;
  // Seller shown when the catalog names none (MPL-3832).
  cateringName?: string;
};

export const DietAddonCardDetails = ({
  addon,
  cateringName,
}: DietAddonCardDetailsProps) => {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const { ref: descriptionRef, isClamped } =
    useIsTextClamped<HTMLParagraphElement>(
      addon.description ?? undefined,
      !expanded,
    );

  // Nothing to toggle when the description is missing or fits in a single line.
  const showToggle = !!addon.description && (expanded || isClamped);

  return (
    <div className="flex w-full items-start gap-4 rounded-xl bg-brand-white p-4">
      <div className="relative size-12 shrink-0 overflow-hidden rounded-lg border border-brand-grey2">
        <Img
          alt={addon.name}
          className="size-full object-cover"
          height={48}
          src={addon.imageUrl ?? "/placeholder.svg"}
          width={48}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col text-brand-green2-300">
        <AddonSellerName fallback={cateringName} name={addon.merchantName} />
        <p className="font-medium text-base leading-6 md:text-lg md:leading-[26px]">
          {addon.name}
        </p>
        <p
          className={cn(
            "text-brand-grey5 text-sm leading-[22px]",
            expanded ? undefined : "line-clamp-1",
          )}
          ref={descriptionRef}
        >
          {addon.description}
        </p>
      </div>

      {showToggle && (
        <button
          aria-expanded={expanded}
          className="shrink-0 font-medium text-base text-brand-green2-300 underline underline-offset-4"
          onClick={() => setExpanded((value) => !value)}
          type="button"
        >
          {expanded
            ? t("cart.configuration.addons.modal.collapse")
            : t("cart.configuration.addons.modal.expand")}
        </button>
      )}
    </div>
  );
};
